import type { CachedRecentSearch } from '@/redux/modules/search/save/saveSearch.api';
import type { GlobalState } from '@/redux/store';
import type { SearchParams } from '@/types/SaveSearch';
import type { SearchUrlParams } from '@/types/search/SearchUrlParams';
import type { FlatFacetForCategoryResolution } from '@/utils/search/searchQuery';

import paramTypes from '@/types/search/enums/paramTypes';
import { mergeCategoryIdsIntoQueryParams } from '@/utils/browsePath/browseCategoryIds';
import { cleanKeywordSearchString } from '@/utils/cleanSearchTerms';
import { getKeywordFromQueryParams, parseQueryParams } from '@/utils/queryParams';
import { collectSaveSearchCategoryIds } from '@/utils/saveSearch/collectSaveSearchCategoryIds';
import { addOrsBetweenKeywords, sanitizeSearchKey } from '@/utils/strings';

export type SaveSearchParamsOrKey = SearchParams | string;

/**
 *
 * @param queryParams SearchUrlParams usually sourced from parseQueryParams
 * @param pathname string pathname to parse categories from, if category or priceguide page for additional category_id parsing
 * @param cleanAndFilter boolean to clean and filter the keyword param
 * @param browseCategoryIds resolved browse category ids from `state.browsePath.baseCategoryIds`
 * @param searchFacets latest search response facets (used on `/search/` to fold semantic dimensions into `category_id`)
 * @returns SearchUrlParams
 */
export const getSaveSearchParams = (
    queryParams: SearchUrlParams,
    pathname: string = '',
    cleanAndFilter: boolean = false,
    browseCategoryIds?: number[],
    searchFacets?: FlatFacetForCategoryResolution[]
): SearchUrlParams | null => {
    const MAX_WORDS = 4;
    const newParams = filterSaveSearchParams(queryParams);

    if (newParams?.keyword) {
        newParams.keyword = cleanAndFilter
            ? addOrsBetweenKeywords(cleanKeywordSearchString(newParams.keyword), MAX_WORDS)
            : newParams.keyword;
    } else {
        delete newParams.keyword;
    }

    const isBrowseLandingPath = pathname.includes('/c/') || pathname.includes('/price-guide/');
    const isKeywordSearchPath = pathname.includes('/search/');
    if (searchFacets?.length && isKeywordSearchPath && !isBrowseLandingPath) {
        const facetCategoryIds = collectSaveSearchCategoryIds(
            queryParams as Record<string, unknown>,
            pathname,
            searchFacets
        );
        if (facetCategoryIds.length > 0) {
            Object.assign(newParams, mergeCategoryIdsIntoQueryParams(newParams, facetCategoryIds));
        }
    }

    if (isBrowseLandingPath && !queryParams.campaign_id) {
        delete newParams.keyword;
        let pathCategoryIds: string[] = [];
        const categoryIdMatches = pathname.matchAll(/\/(\d+)/g);
        for (const categoryId of categoryIdMatches) {
            pathCategoryIds.push(categoryId[1]);
        }
        newParams.category_id = [...pathCategoryIds, newParams.category_id]
            .filter((categoryId) => Boolean(categoryId))
            .join(',');

        // Browse dimensions live in the path (`/c/photography/creator/helmut-newton/`) instead of the
        // query string. `browsePath.baseCategoryIds` already holds the L1 plus creator/material ids
        // from resolve-browse-url, so merging that list keeps Follow Search in sync with the page.
        if (browseCategoryIds && browseCategoryIds.length > 0) {
            newParams.category_id = mergeCategoryIdsIntoQueryParams(newParams, browseCategoryIds).category_id;
        }
    }

    if (pathname.includes('/price-result/') && !queryParams.category_id) {
        newParams.keyword = typeof document !== 'undefined' ? document?.title : '';
    }

    return Object.keys(newParams).length ? newParams : null;
};

/**
 * filterSaveSearchParams - filters given queryParams object and returns SearchUrlParams with allowed
 * parameters only for a Save Search
 * @param queryParams SearchUrlParams
 * @returns SearchUrlParams
 */
const filterSaveSearchParams = (queryParams: SearchUrlParams) => {
    const safeSearchFilters = [
        'category_id',
        'country',
        'current_bid',
        'distance',
        'estimated_price',
        'house_id',
        'keyword',
        'location',
        'payment',
        'sale_type',
        'shipping',
        'topRated',
    ];
    let safeSaveSearchParams: SearchUrlParams = {};
    Object.keys(queryParams).forEach((queryKey) => {
        if (safeSearchFilters.includes(queryKey)) {
            safeSaveSearchParams[queryKey] = queryParams[queryKey];
        }
    });
    return safeSaveSearchParams;
};

export const createSaveSearchParams = (categoryId: string) => {
    return {
        category_id: categoryId,
    };
};

export const createSaveSearchKey = (categoryId: string) => {
    return createSaveSearchKeyBySearchParams(createSaveSearchParams(categoryId));
};

// savedsearch-api sorts `category_id` before hashing, so the same followed search can come back from the list
// in a different id order than the page builds; the key has to ignore that order too.
const sortCategoryIds = (categoryIds: string): string =>
    categoryIds
        .split(',')
        .map((categoryId) => categoryId.trim())
        .filter(Boolean)
        .sort((first, second) => first.localeCompare(second, 'en', { numeric: true }))
        .join(',');

export const createSaveSearchKeyBySearchParams = (searchParams: SearchParams): string => {
    if (!searchParams) {
        return '';
    }
    const sortedParams = {};
    Object.keys(searchParams)
        .sort()
        .forEach((key: string) => {
            if (key === 'status') {
                return;
            }
            const value = (typeof searchParams?.[key] === 'string' ? searchParams[key] : '')
                .replace(/\s+/g, ' ')
                .trim();
            sortedParams[key] = (key === paramTypes.CATEGORY_ID ? sortCategoryIds(value) : value) || sortedParams[key];
        });

    const searchKey = sanitizeSearchKey(JSON.stringify(sortedParams).replace(/}|{|"/g, ''));
    return searchKey;
};

/**
 * The `byParams` key a search is followed under, whether the caller has its params or already has its key.
 */
export const getSaveSearchKey = (saveSearchParams: SaveSearchParamsOrKey): string =>
    typeof saveSearchParams !== 'string'
        ? createSaveSearchKeyBySearchParams(saveSearchParams)
        : sanitizeSearchKey(saveSearchParams);

const normalizeRecentSearchKeyword = (keyword: string) => keyword.trim().toLowerCase();

export const getRecentSearchKeywordFromParams = (saveSearchParams: SaveSearchParamsOrKey): string => {
    if (typeof saveSearchParams === 'string') {
        return saveSearchParams.trim();
    }

    const keyword = saveSearchParams.keyword;

    return typeof keyword === 'string' ? keyword.trim() : String(keyword ?? '').trim();
};

/**
 * Stays tolerant of a malformed entry on purpose. This feeds the header, which renders on every
 * page, so a throw here takes the whole app down through the root ErrorBoundary. A bad entry has
 * to degrade to a missing chip instead.
 */
export const getRecentSearchQuery = (recentSearch: CachedRecentSearch | null | undefined): string => {
    if (!recentSearch) {
        return '';
    }

    const keywordFromFacets = getKeywordFromQueryParams(recentSearch.facets);

    if (keywordFromFacets) {
        return String(keywordFromFacets);
    }

    const url = recentSearch.url ?? '';
    const queryString = url.includes('?') ? url.split('?')[1] : '';
    const keywordFromUrl = getKeywordFromQueryParams(parseQueryParams(queryString));

    return keywordFromUrl ? String(keywordFromUrl) : '';
};

export const isKeywordInRecentSearches = (state: GlobalState, keyword: string): boolean => {
    const normalizedKeyword = normalizeRecentSearchKeyword(keyword);

    if (!normalizedKeyword) {
        return false;
    }

    return state.saveSearch.recentSearches.some((recentSearch) => {
        const query = getRecentSearchQuery(recentSearch);

        return normalizeRecentSearchKeyword(query) === normalizedKeyword;
    });
};
